import React from 'react';
import { Helmet as ReactHelmetAsync } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { useCopyContext } from '../../context/CopyContext';
import { usePageMeta } from '../../hooks';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { generateUrlWithShareParameter } from '../../utils/generateUrlWithShareParameter';
import { makePhotoUrl } from '../../utils/makePhotoUrl';

export function Helmet() {
  const { data: basePageData } = useBasePageQuery();
  const { currentUrl, metaTagImageUrl } = usePageMeta();
  const { helmet } = useCopyContext();
  const chromeFeaturedItemMetaTags = useChromeFeaturedItemMetaTags();

  const imageUrl =
    metaTagImageUrl ||
    (basePageData?.page?.cover?.__typename === 'ImageMedia' &&
      basePageData?.page?.cover?.url) ||
    makePhotoUrl('trainer-no-branding_web.jpg');

  const canonicalUrl = generateUrlWithShareParameter(
    currentUrl,
    {
      ogImageUrl: metaTagImageUrl,
      ogDescription: helmet.ogTitle,
    },
    false,
  );

  return (
    <ReactHelmetAsync
      title={helmet.title}
      meta={[
        { property: 'og:title', content: helmet.ogTitle },
        {
          name: 'description',
          property: 'og:description',
          content: helmet.description,
        },
        {
          property: 'og:image',
          content: `${imageUrl}?template=size1200x630face`,
        },
        {
          property: 'og:image:secure_url',
          content: `${imageUrl}?template=size1200x630face`,
        },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        { property: 'og:url', content: canonicalUrl },
        { name: 'twitter:card', content: 'summary_large_image' },
        {
          name: 'twitter:image:src',
          content: `${imageUrl}?template=size1200x630face`,
        },
        {
          name: 'twitter:description',
          content: helmet.twitterDescription,
        },
        {
          name: 'twitter:domain',
          content: 'justgiving.com',
        },
        { property: 'og:site_name', content: 'JustGiving' },
        ...chromeFeaturedItemMetaTags,
      ]}
    />
  );
}

function useChromeFeaturedItemMetaTags() {
  const { t } = useTranslation('common', { useSuspense: false });
  const { data: basePageData } = useBasePageQuery();

  if (
    basePageData?.page?.allowFundraising &&
    basePageData?.page?.startFundraisingUrl
  ) {
    return [
      {
        name: 'jg:chrome:featuredItemLabel',
        content: `${t('campaign.startFundraisingCTA')}`,
      },
      {
        name: 'jg:chrome:featuredItemUrl',
        content: basePageData?.page?.startFundraisingUrl,
      },
    ];
  } else {
    return [];
  }
}
